import { Box, Flex, Text } from "@chakra-ui/react";
import { MdOutlineAttachMoney } from "react-icons/md";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { useState } from "react";
import { PayrollCollectionsFilters } from "@/components/consignado/Cobranca/components/PayrollCollectionsFilters";
import { FilterByType, Filters, ICobrancaRelatorio, pageConfigCobranca, PageTypeCobranca } from "@/models/consignado/proposal-billings.model";
import { useQuery } from "@tanstack/react-query";
import { buscarPropostaCobrancasRelatorios } from "@/services/consignado/proposta-cobranca.service";
import RelatorioDetalhe from "@/components/consignado/Cobranca/RelatorioDetalhe";
import { removeMaskCpfCnpj } from "@/utils/removeMask";

type Props = {
  type: PageTypeCobranca;
};

// Página legada da Cobrança. Hoje só o Relatório (layout ANALYTICS) passa por
// aqui — Vínculos Ativos e Encerrados são o redesign (VinculosAtivosPage /
// VinculosEncerradosPage). A reformulação do Relatório está "[A DEFINIR]" no
// EPI-ADM-cobranca-018.
export default function CobrancaPage({ type }: Props) {
  const configPage = pageConfigCobranca[type];
  const [filterBy, setFilterBy] = useState<FilterByType>("competencia");
  const backgroundStyle = useConditionalBackground();
  const initialFilters: Filters = {
    status: '',
    corban: "",
    promotora: "",
    vendedor: "",
    gerente_comercial: "",
    periodo: "",
    empregador: "",
    tomador: "",
    codigo_proposta: "",
    pagina: 1,
    limite: 10,
    data_pagamento_inicio: undefined,
    data_pagamento_fim: undefined,
    data_competencia: configPage.getInitialDataCompetencia(),
    status_competencia: undefined,
    status_envio_email: undefined,
    tipo_disparo_email: undefined,
    order: "ASC",
    vinculo_ativo: configPage.vinculoAtivo,
    novos_vinculos: configPage.filters.novos_vinculos.defaultValue,
    metricas_vinculos: configPage?.metricas?.enabled || undefined
  }
  const [filtersRef, setFiltersRef] = useState<Filters>({ ...initialFilters})
  const [appliedFilters, setAppliedFilters] = useState<Filters>({ ...initialFilters})

  const {data: relatorio, refetch: refetchRelatorio, isLoading, isRefetching } = useQuery<ICobrancaRelatorio[]>({
    queryKey: ['cobranca-relatorio', appliedFilters],
    queryFn: async () => await buscarPropostaCobrancasRelatorios(appliedFilters),
    placeholderData: (prev) => prev,
    retry: 0,
    refetchOnWindowFocus: false
  })

  const buildInitialFilters = (): Filters => ({
    ...initialFilters,
    data_competencia: configPage.getInitialDataCompetencia(),
    vinculo_ativo: configPage.vinculoAtivo,
    novos_vinculos: configPage.filters.novos_vinculos.defaultValue
  });

  const handleClearFilters = () => {
    const newFilters = buildInitialFilters();
    setFiltersRef(newFilters);
    setAppliedFilters(newFilters);
    setFilterBy("competencia")
    refetchRelatorio()
  }

  const handleFilter = () => {
    const tomadorFiltro = filtersRef.tomador?.trim() || "";
    const tomadorSomenteNumeros = removeMaskCpfCnpj(tomadorFiltro);
    const tomador =
      tomadorSomenteNumeros.length >= 11 ? tomadorSomenteNumeros : tomadorFiltro;

    const payload = {
      ...filtersRef,
      pagina: 1,
      tomador,
      tipo_disparo_email: filtersRef.tipo_disparo_email || undefined,
      status_envio_email: filtersRef.status_envio_email || undefined
    };

    setAppliedFilters(payload);
  };

  return (
    <Box w="100%" padding={{ base: "2", md: "4" }}>
      <Box display="flex" flexDirection="column" alignItems="center" width="100%">
        <Box
          {...backgroundStyle}
          borderRadius="md"
          p={{ base: "4", md: "6" }}
          color="white"
          boxShadow="md"
          width="100%"
          mb={6}
        >
          <Box
            alignItems={{ base: "center", md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
            textAlign={{ base: "center", md: "left" }}
          >
            <Flex
              alignItems="center"
              mb={{ base: "4", md: "10" }}
              maxW={{ base: "100%", md: "80%", lg: "70%" }}
            >
              <MdOutlineAttachMoney size="2rem" />
              <Text
                fontSize={{ base: "2xl", md: "4xl" }}
                fontWeight="bold"
                ml={2}
                fontFamily="Lexend Deca"
              >
                {configPage.title}
              </Text>
            </Flex>

            <PayrollCollectionsFilters
              filtersRef={filtersRef}
              setFiltersRef={setFiltersRef}
              handleFilter={handleFilter}
              handleClearFilters={handleClearFilters}
              isLoading={isLoading}
              isRefetching={isRefetching}
              filterBy={filterBy!}
              setFilterBy={setFilterBy!}
              filtersConfig={configPage.filters}
              metricasConfig={configPage?.metricas}
            />
          </Box>
        </Box>

        {relatorio?.length === 0 && (
          <Flex w="100%" minH="40vh" justifyContent="center" alignItems="center">
            <Text
              fontSize="xl"
              fontFamily="Lexend Deca"
              color="gray.400"
              textAlign="center"
            >
              Nenhuma cobrança encontrada
            </Text>
          </Flex>
        )}
      </Box>

      {relatorio && relatorio.length > 0 && (
        <RelatorioDetalhe data={relatorio}/>
      )}
    </Box>
  );
}
